import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { formatINR } from "@/lib/qgCalc";
import { sectionCode } from "@/lib/qgApi";
import { NotesEditor } from "./NotesEditor";
import { ValidationList } from "./ValidationList";

function SummaryTable({ q, calc, onJump }) {
  const bRows = q.sections.filter((s) => s.section === "B");
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-800">
      <table className="w-full" data-testid="qg-summary-table">
        <thead className="bg-slate-900 text-white text-[11px] uppercase tracking-wider">
          <tr><th className="px-4 py-2.5 text-left w-20">Sr. No.</th><th className="px-4 py-2.5 text-left">Description of work</th><th className="px-4 py-2.5 text-right">Amount</th></tr>
        </thead>
        <tbody>
          {q.sections.map((s) => {
            const c = calc.categories[s.key];
            const firstB = s.key === bRows[0]?.key;
            return (
              <tr key={s.key} className="border-b border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/40 cursor-pointer" onClick={() => onJump({ section_key: s.key })} data-testid={`qg-summary-row-${s.key}`}>
                {s.section === "A" && <td className="px-4 py-2.5 font-bold text-center">A</td>}
                {firstB && <td rowSpan={bRows.length} className="px-4 py-2.5 font-bold text-center align-middle border-r border-slate-100 dark:border-slate-700">B</td>}
                <td className="px-4 py-2.5 text-sm"><span className="font-mono text-[11px] text-slate-400 mr-2">{sectionCode(q.sections, s.key)}</span>{["it", "kitchen"].includes(s.key) ? s.name : s.name.toUpperCase()}</td>
                <td className="px-4 py-2.5 text-right tabular-nums text-sm" data-testid={`qg-summary-amount-${s.key}`}>
                  {c.status === "not_applicable" ? <span className="text-slate-400 italic">Not included</span> : formatINR(c.total, true)}
                </td>
              </tr>
            );
          })}
          <tr className="bg-brillien-50 dark:bg-brillien-900/20">
            <td colSpan={2} className="px-4 py-3 font-bold text-slate-900 dark:text-white">TOTAL BOQ VALUE</td>
            <td className="px-4 py-3 text-right font-bold text-lg tabular-nums text-brillien-700" data-testid="qg-summary-total">{formatINR(calc.total, true)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function InteriorRate({ q, calc, readOnly, actions }) {
  const selected = new Set(q.interior_rate_sections || []);
  const toggle = (k, v) => actions.setField("interior_rate_sections", q.sections.map((s) => s.key).filter((x) => (x === k ? v : selected.has(x))));
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 space-y-4" data-testid="qg-interior-rate">
      <div>
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Interior rate (optional)</h3>
        <p className="text-xs text-slate-500">Does not affect the summary or Total BOQ Value, and never blocks generation. Tick the sections that count towards the interior rate.</p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {q.sections.map((s) => (
          <label key={s.key} className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-200">
            <Checkbox checked={selected.has(s.key)} onCheckedChange={(v) => toggle(s.key, !!v)} disabled={readOnly} data-testid={`qg-interior-section-${s.key}`} />
            {sectionCode(q.sections, s.key)} · {s.name}
          </label>
        ))}
      </div>
      <div className="grid sm:grid-cols-3 gap-4 items-end">
        <label className="block space-y-1">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Total interior area (sq.ft)</span>
          <Input value={q.interior_area_sqft || ""} onChange={(e) => actions.setField("interior_area_sqft", e.target.value)} disabled={readOnly} inputMode="decimal" className="rounded-lg" data-testid="qg-interior-area-input" />
        </label>
        <div><p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Total interior rate</p><p className="font-bold tabular-nums" data-testid="qg-interior-total">{formatINR(calc.interior.total, true)}</p></div>
        <div><p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Interior rate per sq.ft</p>
          <p className="font-bold tabular-nums" data-testid="qg-interior-per-sqft">{calc.interior.perSqft === null ? <span className="text-xs font-normal text-slate-400">Enter an area above zero</span> : formatINR(calc.interior.perSqft, true)}</p></div>
      </div>
    </div>
  );
}

export function SummaryPanel({ q, calc, readOnly, actions, onJump }) {
  return (
    <div className="space-y-8" data-testid="qg-summary-panel">
      <header>
        <p className="font-mono text-xs text-brillien-600 font-semibold">REVIEW</p>
        <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Summary — Design &amp; Build</h2>
        <p className="text-sm text-slate-500 mt-1">Each amount comes directly from its section total. Click a row to open that section.</p>
      </header>
      <SummaryTable q={q} calc={calc} onJump={onJump} />
      {!readOnly && <ValidationList errors={calc.errors} onJump={onJump} />}
      <InteriorRate q={q} calc={calc} readOnly={readOnly} actions={actions} />
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
        <NotesEditor title="General notes & terms" notes={q.general_notes} readOnly={readOnly} onChange={(n) => actions.setField("general_notes", n)} testId="qg-general-notes" />
      </div>
    </div>
  );
}
